import { useRef } from 'react';
import { Button } from '../common/Button';

interface ReceiptUploadProps {
  onUpload: (file: File) => void;
  /**
   * F7-005 / F8-006: required parent-supplied error surface. Previously
   * optional with an `alert()` fallback; that fallback let new callers
   * silently regress to the blocking-modal UX the F7-005 fix retired.
   * Required so TypeScript flags unmigrated callers at compile time.
   */
  onError: (message: string) => void;
  isUploading?: boolean;
}

const ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

export default function ReceiptUpload({ onUpload, onError, isUploading }: ReceiptUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      onError('Please upload an image (JPEG, PNG, GIF, WebP) or PDF file.');
      // Reset so the user can pick a different file without re-clicking Upload.
      e.target.value = '';
      return;
    }

    if (file.size > MAX_SIZE) {
      onError('File is too large. Maximum size is 10MB.');
      e.target.value = '';
      return;
    }

    onUpload(file);
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
        onChange={handleChange}
        className="hidden"
      />
      <Button
        size="sm"
        variant="secondary"
        onClick={handleClick}
        isLoading={isUploading}
      >
        Upload
      </Button>
    </>
  );
}
